import { useMemo, useState } from 'react'
import {
	Sheet,
	CollapsibleCard,
	AppButton,
	DataGrid,
	DataGridColumn
} from '@clickguard/design-system'
import { AccountLinkDTO } from 'api-models'
import { useStores } from 'hooks'
import { useUiStore } from 'store'
import { GButton, GDialog } from 'components/basic-blocks'
import { GCheckbox } from 'components/basic-blocks/g-checkbox'
import { GoogleAuthConfig, FacebookAuthConfig, BingAuthConfig } from 'config'
import {
	CredentialIcon,
	credentialEmail,
	credentialLabel
} from './credential-helpers'

type AccountRow = {
	id: string
	name: string
	accountId: string
	website: string
}

const stripAct = (s?: string) => (s ?? '').replace('act_', '')

/**
 * §2.7 — an empty list keeps its table, with a dash in each cell, rather than
 * collapsing to a message. The header still reports `(0)`, which is what says the list
 * is empty; the table stays so the two sections keep the same shape whether or not the
 * credential reaches anything.
 */
// The em dash the Website column already falls back to, so a populated row with no
// website and a wholly empty table draw the same character.
const EMPTY_ROW: AccountRow = {
	id: '__empty__',
	name: '—',
	accountId: '—',
	website: '—'
}
const withEmptyRow = (rows: AccountRow[]): AccountRow[] =>
	rows.length ? rows : [EMPTY_ROW]

/**
 * Credentials configuration drawer ("Credentials settings") — DEV-2352 §3.2.
 * Restyle of the legacy ConfigurePpcAccess; reuses its sync / reauthorize /
 * delete behaviour. The connection-delete confirmation is kept unchanged
 * (existing GDialog + "deactivate all PPC accounts" checkbox).
 */
export const CredentialDrawer = ({
	link,
	open,
	onClose,
	isAnalyst
}: {
	link: AccountLinkDTO | null
	open: boolean
	onClose: () => void
	isAnalyst: boolean
}) => {
	const { accountStore } = useStores()
	const { deleteAccountLink, syncAccountLink, account } = accountStore
	const isLoading = useUiStore((s) => s.isLoading)

	const [openDelete, setOpenDelete] = useState(false)
	const [deactivatePPC, setDeactivatePPC] = useState(false)

	const l = link as
		| (AccountLinkDTO & {
				clients?: Array<{ account_number?: string; name?: string }> | null
				connected_accounts?: Array<any> | null
		  })
		| null

	const isMcc = link?.type === 'mcc'

	const addedRows: AccountRow[] = useMemo(() => {
		return (l?.connected_accounts ?? [])
			.filter((acc: any) => acc?.status === 'ACTIVE')
			.map((acc: any) => ({
				id: acc?.id,
				name: acc?.details?.name ?? '',
				accountId: stripAct(acc?.details?.account_number),
				website: (acc?.assets ?? [])
					.map((a: any) => a?.name)
					.filter(Boolean)
					.join(', ')
			}))
	}, [l])

	const availableRows: AccountRow[] = useMemo(() => {
		return (l?.clients ?? []).map((c, i) => ({
			id: `${c.account_number ?? i}`,
			name: c.name ?? '',
			accountId: stripAct(c.account_number),
			website: ''
		}))
	}, [l])

	if (!link) {
		return (
			<Sheet
				open={open}
				onClose={onClose}
				title="Credentials settings"
				side="right"
			/>
		)
	}

	const email = credentialEmail(link)

	const handleSync = () => syncAccountLink(link.id)

	const handleDelete = async () => {
		await deleteAccountLink(link.id, deactivatePPC)
		setOpenDelete(false)
		onClose()
	}

	const reauthorize = () => {
		window.localStorage.setItem('account-id', account ? account.id : '')
		window.localStorage.setItem('link-id', link.id)
		if (link.platform === 'GADS') {
			window.localStorage.setItem('google-reauth', '1')
			window.open(GoogleAuthConfig.AUTH_URL, '_blank')
		} else if (link.platform === 'FADS') {
			window.localStorage.setItem('facebook-reauth', '1')
			window.open(FacebookAuthConfig.AUTH_URL, '_blank')
		} else if (link.platform === 'BADS') {
			window.localStorage.setItem('bing-reauth', '1')
			window.open(BingAuthConfig.AUTH_URL, '_blank')
		}
	}

	const accountColumns: DataGridColumn<AccountRow>[] = [
		{
			id: 'name',
			header: 'Account name',
			cell: (r) => <span className="text-t-default text-sm">{r.name}</span>
		},
		{
			id: 'accountId',
			header: 'Account ID',
			cell: (r) => (
				<span className="text-t-secondary text-sm">{r.accountId}</span>
			)
		},
		{
			id: 'website',
			header: 'Website',
			cell: (r) => (
				<span className="text-t-secondary text-sm truncate max-w-[200px]">
					{r.website || '—'}
				</span>
			)
		}
	]

	// DEV-2422 §2.7 / D10 — an MCC gets `Reauthorize` too. The shipped page gated it on
	// `!isMcc`, which left an MCC that lost access reporting a fault with no way out.
	// The flow works: reauthorizing re-consents the PARENT Google link
	// (`createOAuthLink` refreshes its token, `link-service.ts:109`), then the MCC sync
	// copies that token over (`links-api.ts:531`) and flips the status back to OK.
	const needsReauthorize = link.status === 'UNLINKED'

	const reauthError =
		link.status === 'UNLINKED'
			? link.platform === 'GADS'
				? 'The access token was revoked'
				: isMcc
				? 'Parent connection lost access to this MCC account'
				: ''
			: ''

	return (
		<Sheet
			open={open}
			onClose={onClose}
			title="Credentials settings"
			side="right"
			width={640}
		>
			<div className="flex flex-col gap-4 p-1">
				<CollapsibleCard
					title={`${credentialLabel(link)} credential`}
					defaultExpanded
				>
					{/* Header card. Name over email, and ONE action — `Reauthorize` while the
					    credential is broken, `Synchronize` otherwise, never both. */}
					<div className="flex items-center justify-between gap-3 border border-t-traffic rounded-xl px-4 py-3">
						<div className="flex min-w-0 items-center gap-3">
							<CredentialIcon link={link} />
							<div className="flex min-w-0 flex-col">
								<span className="truncate text-t-default text-sm font-medium">
									{credentialLabel(link)}
								</span>
								<span className="truncate text-t-secondary text-xs">
									{email}
								</span>
								{/* The reason sits under the email it belongs to, not in a
								    section of its own further down the sheet. */}
								{reauthError && (
									<span className="text-xs font-medium text-[rgb(var(--cg-danger))]">
										{reauthError}
									</span>
								)}
							</div>
						</div>
						{needsReauthorize ? (
							<AppButton
								variant="secondary"
								size="sm"
								label="Reauthorize"
								disabled={isAnalyst}
								onClick={reauthorize}
							/>
						) : (
							<AppButton
								loader="dots"
								variant="secondary"
								size="sm"
								label="Synchronize"
								disabled={isAnalyst}
								isLoading={isLoading('ACCOUNT_SYNC_ACCESS')}
								onClick={handleSync}
							/>
						)}
					</div>

					{/* §2.7 — the two tables belong to one section, and the section says what
					    it is listing. Without this header the reader meets "Added accounts"
					    and "Available accounts" with nothing saying added to WHAT. */}
					<div className="mt-6">
						<p className="text-sm font-medium text-t-default">Accounts</p>
						<p className="text-xs text-t-secondary">
							Ad accounts this credential can reach.
						</p>
					</div>

					{/* Added accounts */}
					<div className="mt-4">
						<p className="text-sm font-medium text-t-default mb-2">
							Added accounts ({addedRows.length})
						</p>
						<DataGrid<AccountRow>
							columns={accountColumns}
							data={withEmptyRow(addedRows)}
							getRowId={(r) => r.id}
							pageSize={10}
							pageSizeOptions={[10, 20, 50]}
						/>
					</div>

					{/* Available accounts */}
					<div className="mt-6">
						<p className="text-sm font-medium text-t-default mb-2">
							Available accounts ({availableRows.length})
						</p>
						<DataGrid<AccountRow>
							columns={accountColumns}
							data={withEmptyRow(availableRows)}
							getRowId={(r) => r.id}
							pageSize={10}
							pageSizeOptions={[10, 20, 50]}
						/>
					</div>
				</CollapsibleCard>

				<CollapsibleCard title="Danger Zone" defaultExpanded>
					<div className="flex items-center justify-between gap-4">
						<div className="flex min-w-0 flex-col">
							<span className="text-sm font-medium text-[rgb(var(--cg-danger))]">
								Delete connection
							</span>
							{/* §2.7 — say what deleting costs, not just what it is called. */}
							<span className="text-xs text-t-secondary">
								This removes the connection. ClickGuard then loses access to
								every account it reads.
							</span>
						</div>
						<GButton
							disabled={isAnalyst}
							label="Delete"
							color={isAnalyst ? 'neutral' : 'danger'}
							className="h-[36px] px-5 text-white"
							labelClassName="font-bold"
							variant="contained"
							size="sm"
							loading={isLoading('ACCOUNT_REMOVE_ACCESS')}
							onClick={() => setOpenDelete(true)}
						/>
					</div>
				</CollapsibleCard>
			</div>

			{/* Existing delete confirmation — kept unchanged (DEV-2352 PO decision) */}
			<GDialog
				open={isAnalyst ? false : openDelete}
				onClose={() => setOpenDelete(false)}
				showClose={false}
				maxWidth="2xl"
			>
				<div>
					<p className="font-bold text-xl border-b pb-4">Delete connection</p>
					<p className="py-6">
						By removing this connection ClickGUARD will not be able to access
						your PPC accounts. Are you sure you want to delete this connection?{' '}
					</p>
					<div className="flex items-center py-2">
						<GCheckbox
							name="deactivate"
							checked={deactivatePPC}
							onChange={(v) => setDeactivatePPC(v.target.checked)}
						/>
						<p className="font-medium">
							Deactivate all added PPC accounts using this connection
						</p>
					</div>
					<div className="flex justify-between items-center pt-6">
						<GButton
							disabled={isAnalyst}
							label="Cancel"
							color="neutral"
							labelClassName="font-bold"
							variant="text"
							size="md"
							onClick={() => setOpenDelete(false)}
						/>
						<GButton
							disabled={isAnalyst}
							label="Delete"
							color={isAnalyst ? 'neutral' : 'danger'}
							variant="contained"
							size="md"
							loading={isLoading('ACCOUNT_REMOVE_ACCESS')}
							onClick={handleDelete}
						/>
					</div>
				</div>
			</GDialog>
		</Sheet>
	)
}
